import React from 'react';
import { motion } from 'framer-motion';
import { GlobeAltIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useFinancialModel } from './FinancialModelContext';

const GTMv2Slide06ThankYou: React.FC = () => {
  const { calculations, revenueSettings, seedInvestment } = useFinancialModel();

  // Get Y3 data (Q4 of Year 3 = index 11)
  const y3Schools = revenueSettings.b2bSchoolsByQuarter[11];
  const y3Subscribers = revenueSettings.b2cSubsByQuarter[11];
  const y3Revenue = calculations.yearSummaries[2]?.totalRevenue || 0;
  const y3Net = calculations.y3Total;

  // Format helpers
  const formatShort = (val: number) => {
    if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (Math.abs(val) >= 1000) return `$${Math.round(val / 1000)}K`;
    return `$${val}`;
  };

  const formatSubs = (val: number) => {
    if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
    return val.toString();
  };

  // Format seed investment range
  const seedMin = Math.max(500000, seedInvestment - 500000);
  const seedMax = seedInvestment + 500000;
  const seedRange = `$${(seedMin / 1000000).toFixed(1)}M – $${(seedMax / 1000000).toFixed(1)}M`;

  const highlights = [
    { label: 'Raising', value: seedRange, color: '#8b5cf6' },
    { label: 'Y3 Schools', value: y3Schools.toString(), color: '#10b981' },
    { label: 'Y3 Subscribers', value: formatSubs(y3Subscribers), color: '#0ea5e9' },
    { label: 'Y3 Revenue', value: formatShort(y3Revenue), color: '#f59e0b' },
    { label: 'Y3 Net', value: formatShort(y3Net), color: '#10b981' },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-12">
      {/* Lumen8 Logo */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <span className="relative inline-block">
          <span
            className="absolute inset-0 text-[9rem] font-black text-[#06b6d4] blur-2xl opacity-40"
            aria-hidden="true"
          >
            Lumen8
          </span>
          <span className="relative text-[9rem] font-black bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent">
            Lumen8
          </span>
        </span>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="text-4xl text-[#94a3b8] font-light mb-10"
      >
        Thank You
      </motion.h2>

      {/* Key Highlights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="flex flex-wrap justify-center gap-8 mb-12"
      >
        {highlights.map((item) => (
          <div
            key={item.label}
            className="px-8 py-5 rounded-2xl min-w-[140px]"
            style={{
              background: `${item.color}15`,
              border: `2px solid ${item.color}50`,
            }}
          >
            <div className="text-3xl font-black" style={{ color: item.color }}>{item.value}</div>
            <div className="text-base text-[#94a3b8]">{item.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Contact */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        className="flex items-center gap-10 mb-10"
      >
        <a
          href="https://lumen8.it"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-8 py-4 rounded-xl transition-all hover:scale-105"
          style={{
            background: 'rgba(16, 185, 129, 0.2)',
            border: '2px solid rgba(16, 185, 129, 0.4)',
          }}
        >
          <GlobeAltIcon className="w-7 h-7 text-[#10b981]" />
          <span className="text-xl text-white">lumen8.it</span>
          <ArrowTopRightOnSquareIcon className="w-5 h-5 text-[#10b981]" />
        </a>
      </motion.div>

      {/* Tagline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
        className="mb-8"
      >
        <p className="text-2xl text-[#64748b] italic">
          "Capturing the Australian K-12 market through a dual B2B + B2C strategy"
        </p>
      </motion.div>

      {/* Investment Ask */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.1 }}
        className="px-12 py-6 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(16, 185, 129, 0.25) 100%)',
          border: '2px solid rgba(139, 92, 246, 0.5)',
        }}
      >
        <p className="text-2xl text-white">
          Seeking <span className="font-black text-[#8b5cf6] text-3xl">{seedRange}</span> Seed Round
        </p>
        <p className="text-lg text-[#94a3b8] mt-2">
          To execute our dual GTM strategy and capture market leadership
        </p>
      </motion.div>
    </div>
  );
};

export default GTMv2Slide06ThankYou;
